import React, { useEffect, useState, useRef } from 'react';
import { MicIcon, SendIcon, SearchIcon, ClockIcon, InfoIcon, FileTextIcon, PlusIcon, CheckCircleIcon, BarChartIcon, ArrowRightIcon, XIcon } from 'lucide-react';
// Sample data to simulate real invoice database
const invoiceDatabase = [{
  id: 'INV-2023-0045',
  vendor: 'Dell Technologies',
  amount: 24500.0,
  date: '2023-10-15',
  status: 'Approved',
  description: 'Server hardware and enterprise licenses'
}, {
  id: 'INV-2023-0038',
  vendor: 'Server Solutions Inc.',
  amount: 12500.0,
  date: '2023-10-10',
  status: 'Pending Approval',
  description: 'Cloud infrastructure services - Q4'
}, {
  id: 'INV-2023-0031',
  vendor: 'Global IT Services',
  amount: 18750.0,
  date: '2023-09-28',
  status: 'Paid',
  description: 'Annual software maintenance contract'
}, {
  id: 'INV-2023-0029',
  vendor: 'Acme Consulting',
  amount: 15200.0,
  date: '2023-09-20',
  status: 'Approved',
  description: 'ERP implementation services'
}, {
  id: 'INV-2023-0027',
  vendor: 'TechCorp Solutions',
  amount: 9800.0,
  date: '2023-09-15',
  status: 'Paid',
  description: 'Hardware upgrades'
}, {
  id: 'INV-2023-0022',
  vendor: 'Office Depot',
  amount: 3250.0,
  date: '2023-09-05',
  status: 'Paid',
  description: 'Office supplies and equipment'
}, {
  id: 'INV-2023-0021',
  vendor: 'Marketing Experts LLC',
  amount: 11800.0,
  date: '2023-09-01',
  status: 'Paid',
  description: 'Q3 marketing campaign'
}, {
  id: 'INV-2023-0019',
  vendor: 'Data Analytics Partners',
  amount: 16500.0,
  date: '2023-08-25',
  status: 'Approved',
  description: 'Business intelligence platform - annual license'
}];
// Sample responses for demonstration purposes
const sampleResponses = {
  'show overdue pos for entity abc': {
    type: 'search_results',
    title: 'Overdue POs for Entity ABC',
    data: [{
      id: 'PO-2023-0027',
      vendor: 'Office Supplies Co.',
      amount: 3450.0,
      dueDate: '2023-10-05',
      status: 'Overdue'
    }, {
      id: 'PO-2023-0019',
      vendor: 'Marketing Solutions',
      amount: 7800.0,
      dueDate: '2023-10-01',
      status: 'Overdue'
    }],
    actions: ['Send Reminder', 'View All']
  },
  "what's the status of inv-84721": {
    type: 'document_detail',
    title: 'Invoice INV-84721 Status',
    data: {
      id: 'INV-84721',
      vendor: 'PrintWorks Inc.',
      amount: 3200.5,
      date: '2023-09-20',
      status: 'Pending Approval',
      approver: 'John Smith',
      notes: 'Waiting for receipt matching confirmation',
      timeline: [{
        date: '2023-09-20',
        action: 'Invoice received',
        user: 'System'
      }, {
        date: '2023-09-21',
        action: 'Sent for approval',
        user: 'Jane Doe'
      }]
    },
    actions: ['View Details', 'Approve', 'Reject']
  },
  'create a new requisition': {
    type: 'action_prompt',
    title: 'Create New Requisition',
    message: 'I can help you start a new requisition. Would you like to:',
    options: [{
      label: 'Create a blank requisition',
      action: 'create-requisition'
    }, {
      label: 'Create from template',
      action: 'requisition-templates'
    }, {
      label: 'Create from previous requisition',
      action: 'previous-requisitions'
    }]
  },
  'summarize the status of requisition req-1009': {
    type: 'summary',
    title: 'Requisition REQ-1009 Summary',
    data: {
      id: 'REQ-1009',
      title: 'Office Equipment',
      created: '2023-10-01',
      status: 'Approved',
      totalItems: 5,
      totalValue: 4350.0,
      nextStep: 'Convert to PO',
      approvers: ['Jane Doe', 'Michael Johnson'],
      notes: 'Approved with condition to source from preferred vendors'
    }
  },
  'what did i approve last week': {
    type: 'activity_log',
    title: 'Your Approvals Last Week',
    data: [{
      date: '2023-10-12',
      document: 'INV-2023-0042',
      type: 'Invoice',
      vendor: 'Office Depot',
      amount: 1250.75
    }, {
      date: '2023-10-10',
      document: 'REQ-2023-0037',
      type: 'Requisition',
      description: 'Marketing Materials',
      amount: 3200.5
    }, {
      date: '2023-10-09',
      document: 'PO-2023-0024',
      type: 'Purchase Order',
      vendor: 'Tech Solutions Inc.',
      amount: 7800.0
    }]
  },
  'what does ready for matching mean': {
    type: 'explanation',
    title: 'Status: Ready for Matching',
    content: "The status 'Ready for Matching' indicates that an invoice has been received and validated for basic information, but still needs to be matched against a Purchase Order (PO) and/or Goods Receipt Note (GRN) before it can be approved for payment. This is part of the 3-way matching process that ensures you only pay for goods that were ordered and received as expected. During this stage, the system is looking for corresponding PO numbers and receipt confirmations."
  }
};
// Default suggested prompts
const suggestedPrompts = ['Find all invoices over $10,000', 'Show overdue POs for Entity ABC', "What's the status of INV-84721?", 'Create a new requisition', 'Summarize the status of Requisition REQ-1009', 'What did I approve last week?', "What does 'Ready for Matching' mean?"];
export function Remi({
  setCurrentView
}) {
  const [query, setQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [response, setResponse] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);
  const inputRef = useRef(null);
  // Focus the input when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  // Function to find invoices over a certain amount
  const findInvoicesOverAmount = amount => {
    const filteredInvoices = invoiceDatabase.filter(invoice => invoice.amount > amount);
    return {
      type: 'search_results',
      title: `Invoices over $${amount.toLocaleString()}`,
      data: filteredInvoices,
      actions: ['View All', 'Export to CSV'],
      searchType: 'high_value_invoices'
    };
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (!query.trim()) return;
    // Add to history
    setQueryHistory([...queryHistory, query]);
    // Simulate processing
    setIsProcessing(true);
    setTimeout(() => {
      let result;
      // Check if the query is about finding invoices over a certain amount
      const invoiceAmountMatch = query.toLowerCase().match(/find (?:all )?invoices over \$?(\d+[,\d]*)/);
      if (invoiceAmountMatch) {
        // Extract the amount from the query
        const amountStr = invoiceAmountMatch[1].replace(/,/g, '');
        const amount = parseFloat(amountStr);
        if (!isNaN(amount)) {
          // Perform actual search
          result = findInvoicesOverAmount(amount);
        }
      } else {
        // Look for exact matches in predefined responses
        result = sampleResponses[query.toLowerCase()];
        // If no exact match, try to find partial matches
        if (!result) {
          for (const key of Object.keys(sampleResponses)) {
            if (query.toLowerCase().includes(key) || key.includes(query.toLowerCase())) {
              result = sampleResponses[key];
              break;
            }
          }
        }
      }
      // If still no match, provide a default response
      if (!result) {
        result = {
          type: 'no_results',
          title: "I'm not sure how to help with that",
          message: "I couldn't find information related to your query. Would you like to try a different question or search term?",
          suggestions: suggestedPrompts.slice(0, 3)
        };
      }
      setResponse(result);
      setIsProcessing(false);
    }, 1000);
  };
  const handleSuggestedPrompt = prompt => {
    setQuery(prompt);
    // Focus on input after setting the query
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  const handleAction = action => {
    if (action === 'create-requisition') {
      setCurrentView('create-requisition');
    } else if (action.startsWith('view-')) {
      const documentId = action.replace('view-', '');
      // Handle viewing different document types
      if (documentId.startsWith('INV-')) {
        setCurrentView('invoice-list');
      } else if (documentId.startsWith('PO-')) {
        setCurrentView('purchase-orders');
      } else if (documentId.startsWith('REQ-')) {
        setCurrentView('requisitions');
      }
    }
  };
  const renderResponse = () => {
    if (!response) return null;
    switch (response.type) {
      case 'search_results':
        return <div className="mt-4" data-id="element-166">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-167">
              {response.title}
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-168">
              <ul className="divide-y divide-gray-200" data-id="element-169">
                {response.data.map((item, index) => <li key={index} className="px-4 py-3 sm:px-6 hover:bg-gray-50" data-id="element-170">
                    <div className="flex items-center justify-between" data-id="element-171">
                      <div className="flex items-center" data-id="element-172">
                        <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-173" />
                        <div data-id="element-174">
                          <p className="text-sm font-medium text-indigo-600" data-id="element-175">
                            {item.id}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-176">
                            {item.vendor || item.description}
                          </p>
                          {response.searchType === 'high_value_invoices' && item.description && <p className="text-xs text-gray-500 mt-1" data-id="element-177">
                                {item.description}
                              </p>}
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-178">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'Approved' ? 'bg-green-100 text-green-800' : item.status === 'Overdue' ? 'bg-red-100 text-red-800' : item.status === 'Paid' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-179">
                          {item.status}
                        </span>
                        <span className="ml-2 text-sm text-gray-500" data-id="element-180">
                          $
                          {item.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2
                      })}
                        </span>
                      </div>
                    </div>
                  </li>)}
              </ul>
            </div>
            <div className="mt-3 flex space-x-3" data-id="element-181">
              {response.actions.map((action, index) => <button key={index} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-182">
                  {action}
                </button>)}
            </div>
          </div>;
      case 'document_detail':
        return <div className="mt-4" data-id="element-183">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-184">
              {response.title}
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-185">
              <div className="px-4 py-5 sm:px-6" data-id="element-186">
                <div className="grid grid-cols-2 gap-4" data-id="element-187">
                  <div data-id="element-188">
                    <p className="text-sm font-medium text-gray-500" data-id="element-189">
                      Invoice ID
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-190">
                      {response.data.id}
                    </p>
                  </div>
                  <div data-id="element-191">
                    <p className="text-sm font-medium text-gray-500" data-id="element-192">Status</p>
                    <p className="mt-1 text-sm" data-id="element-193">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-194">
                        {response.data.status}
                      </span>
                    </p>
                  </div>
                  <div data-id="element-195">
                    <p className="text-sm font-medium text-gray-500" data-id="element-196">Vendor</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-197">
                      {response.data.vendor}
                    </p>
                  </div>
                  <div data-id="element-198">
                    <p className="text-sm font-medium text-gray-500" data-id="element-199">Amount</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-200">
                      $
                      {response.data.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                    </p>
                  </div>
                  <div data-id="element-201">
                    <p className="text-sm font-medium text-gray-500" data-id="element-202">Date</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-203">
                      {response.data.date}
                    </p>
                  </div>
                  <div data-id="element-204">
                    <p className="text-sm font-medium text-gray-500" data-id="element-205">
                      Approver
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-206">
                      {response.data.approver}
                    </p>
                  </div>
                </div>
                <div className="mt-4" data-id="element-207">
                  <p className="text-sm font-medium text-gray-500" data-id="element-208">Notes</p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-209">
                    {response.data.notes}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-210">
                <h5 className="text-sm font-medium text-gray-900 mb-2" data-id="element-211">
                  Timeline
                </h5>
                <ul className="space-y-2" data-id="element-212">
                  {response.data.timeline.map((event, index) => <li key={index} className="text-sm" data-id="element-213">
                      <div className="flex items-start" data-id="element-214">
                        <div className="flex-shrink-0 h-4 w-4 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5" data-id="element-215">
                          <div className="h-2 w-2 rounded-full bg-indigo-600" data-id="element-216"></div>
                        </div>
                        <div className="ml-2" data-id="element-217">
                          <p className="font-medium text-gray-900" data-id="element-218">
                            {event.date}
                          </p>
                          <p className="text-gray-500" data-id="element-219">
                            {event.action} by {event.user}
                          </p>
                        </div>
                      </div>
                    </li>)}
                </ul>
              </div>
            </div>
            <div className="mt-3 flex space-x-3" data-id="element-220">
              {response.actions.map((action, index) => <button key={index} className={`inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${action === 'Approve' ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500' : action === 'Reject' ? 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'}`} data-id="element-221">
                  {action}
                </button>)}
            </div>
          </div>;
      case 'action_prompt':
        return <div className="mt-4" data-id="element-222">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-223">
              {response.title}
            </h4>
            <p className="text-sm text-gray-500 mb-3" data-id="element-224">{response.message}</p>
            <div className="space-y-2" data-id="element-225">
              {response.options.map((option, index) => <button key={index} onClick={() => handleAction(option.action)} className="w-full text-left flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-226">
                  <span className="flex items-center" data-id="element-227">
                    <PlusIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-228" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-229">
                      {option.label}
                    </span>
                  </span>
                  <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-230" />
                </button>)}
            </div>
          </div>;
      case 'summary':
        return <div className="mt-4" data-id="element-231">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-232">
              {response.title}
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-233">
              <div className="px-4 py-5 sm:px-6" data-id="element-234">
                <div className="grid grid-cols-2 gap-4" data-id="element-235">
                  <div data-id="element-236">
                    <p className="text-sm font-medium text-gray-500" data-id="element-237">
                      Requisition ID
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-238">
                      {response.data.id}
                    </p>
                  </div>
                  <div data-id="element-239">
                    <p className="text-sm font-medium text-gray-500" data-id="element-240">Status</p>
                    <p className="mt-1 text-sm" data-id="element-241">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-242">
                        {response.data.status}
                      </span>
                    </p>
                  </div>
                  <div data-id="element-243">
                    <p className="text-sm font-medium text-gray-500" data-id="element-244">Title</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-245">
                      {response.data.title}
                    </p>
                  </div>
                  <div data-id="element-246">
                    <p className="text-sm font-medium text-gray-500" data-id="element-247">Created</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-248">
                      {response.data.created}
                    </p>
                  </div>
                  <div data-id="element-249">
                    <p className="text-sm font-medium text-gray-500" data-id="element-250">
                      Total Value
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-251">
                      $
                      {response.data.totalValue.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                    </p>
                  </div>
                  <div data-id="element-252">
                    <p className="text-sm font-medium text-gray-500" data-id="element-253">Items</p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-254">
                      {response.data.totalItems}
                    </p>
                  </div>
                </div>
                <div className="mt-4" data-id="element-255">
                  <p className="text-sm font-medium text-gray-500" data-id="element-256">Next Step</p>
                  <p className="mt-1 text-sm text-indigo-600 font-medium" data-id="element-257">
                    {response.data.nextStep}
                  </p>
                </div>
                <div className="mt-4" data-id="element-258">
                  <p className="text-sm font-medium text-gray-500" data-id="element-259">Approvers</p>
                  <div className="mt-1 flex items-center space-x-2" data-id="element-260">
                    {response.data.approvers.map((approver, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-261">
                        <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-262" />
                        {approver}
                      </span>)}
                  </div>
                </div>
                <div className="mt-4" data-id="element-263">
                  <p className="text-sm font-medium text-gray-500" data-id="element-264">Notes</p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-265">
                    {response.data.notes}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-3 flex space-x-3" data-id="element-266">
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-267">
                View Details
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-268">
                Convert to PO
              </button>
            </div>
          </div>;
      case 'activity_log':
        return <div className="mt-4" data-id="element-269">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-270">
              {response.title}
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-271">
              <ul className="divide-y divide-gray-200" data-id="element-272">
                {response.data.map((item, index) => <li key={index} className="px-4 py-3 sm:px-6 hover:bg-gray-50" data-id="element-273">
                    <div className="flex items-center justify-between" data-id="element-274">
                      <div className="flex items-center" data-id="element-275">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-276" />
                        <div data-id="element-277">
                          <p className="text-sm font-medium text-indigo-600" data-id="element-278">
                            {item.document}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-279">
                            {item.type}: {item.vendor || item.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-280">
                        <span className="text-sm text-gray-500 mr-3" data-id="element-281">
                          $
                          {item.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2
                      })}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-282">
                          {item.date}
                        </span>
                      </div>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>;
      case 'explanation':
        return <div className="mt-4" data-id="element-283">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-284">
              {response.title}
            </h4>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded" data-id="element-285">
              <div className="flex" data-id="element-286">
                <div className="flex-shrink-0" data-id="element-287">
                  <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-288" />
                </div>
                <div className="ml-3" data-id="element-289">
                  <p className="text-sm text-blue-700" data-id="element-290">{response.content}</p>
                </div>
              </div>
            </div>
          </div>;
      case 'no_results':
        return <div className="mt-4" data-id="element-291">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-292">
              {response.title}
            </h4>
            <p className="text-sm text-gray-500 mb-4" data-id="element-293">{response.message}</p>
            <div className="space-y-2" data-id="element-294">
              <p className="text-sm font-medium text-gray-700" data-id="element-295">
                Try one of these instead:
              </p>
              {response.suggestions.map((suggestion, index) => <button key={index} onClick={() => handleSuggestedPrompt(suggestion)} className="block w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md" data-id="element-296">
                  {suggestion}
                </button>)}
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-white shadow-md rounded-lg overflow-hidden" data-id="element-297">
      <div className="px-4 py-4 bg-indigo-600 text-white flex justify-between items-center" data-id="element-298">
        <div className="flex items-center" data-id="element-299">
          <span className="text-xl mr-2" data-id="element-300">🤖</span>
          <h3 className="text-lg font-medium" data-id="element-301">Remi – Ask Me Anything</h3>
        </div>
        <div className="flex items-center space-x-2" data-id="element-302">
          <button onClick={() => setShowHistory(!showHistory)} className="p-1.5 rounded-full text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-white" data-id="element-303">
            <ClockIcon className="h-5 w-5" data-id="element-304" />
          </button>
        </div>
      </div>
      {showHistory && queryHistory.length > 0 && <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-305">
          <div className="flex justify-between items-center mb-2" data-id="element-306">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-307">
              Recent Queries
            </h4>
            <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-500" data-id="element-308">
              <XIcon className="h-4 w-4" data-id="element-309" />
            </button>
          </div>
          <div className="space-y-1" data-id="element-310">
            {queryHistory.slice(-5).reverse().map((q, index) => <button key={index} onClick={() => handleSuggestedPrompt(q)} className="block w-full text-left px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded" data-id="element-311">
                  {q}
                </button>)}
          </div>
        </div>}
      <div className="p-4" data-id="element-312">
        <form onSubmit={handleSubmit} className="flex items-center space-x-2" data-id="element-313">
          <div className="relative flex-grow" data-id="element-314">
            <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Ask me to find something or do something..." className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-315" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-316">
              <MicIcon className="h-4 w-4 text-gray-400 cursor-pointer hover:text-indigo-500" data-id="element-317" />
            </div>
          </div>
          <button type="submit" disabled={isProcessing} className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white ${isProcessing ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-318">
            {isProcessing ? <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-319">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-320"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-321"></path>
              </svg> : <SendIcon className="h-4 w-4" data-id="element-322" />}
          </button>
        </form>
        {!response && !isProcessing && <div className="mt-3" data-id="element-323">
            <p className="text-xs text-gray-500 mb-2" data-id="element-324">Try asking:</p>
            <div className="flex flex-wrap gap-2" data-id="element-325">
              {suggestedPrompts.slice(0, 4).map((prompt, index) => <button key={index} onClick={() => handleSuggestedPrompt(prompt)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded-full" data-id="element-326">
                  {prompt}
                </button>)}
            </div>
          </div>}
        {isProcessing && <div className="flex justify-center items-center h-24" data-id="element-327">
            <div className="animate-pulse flex space-x-4" data-id="element-328">
              <div className="flex-1 space-y-2 py-1" data-id="element-329">
                <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-330"></div>
                <div className="h-2 bg-gray-200 rounded" data-id="element-331"></div>
                <div className="h-2 bg-gray-200 rounded w-5/6" data-id="element-332"></div>
              </div>
            </div>
          </div>}
        {renderResponse()}
      </div>
    </div>;
}